import React, { useEffect, useState, useCallback } from 'react';

import { Stack } from '@mui/system';
import { Box, IconButton, Tooltip, Snackbar, Alert, Dialog, DialogContent, DialogTitle, IconButton as MuiIconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { PostData } from '../model/types';
import VotesList from '../Votes/VotesList';
import VoteButton from '../Votes/VoteButton';
import SubmitPostComponent from '../SubmitPostComponent/SubmitPostComponent';
import Replies from './RepliesComponent';
import HourglassFullIcon from '../resource/HourglassFullIcon';
import RepliesIcon from '../resource/RepliesIcon';
import LinkIcon from '../resource/LinkIcon';
import { AuthContext, MyContext } from '../context/compatibility';
import { CacheContext, CacheContextType } from '../context/CacheContext';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CodeIcon from '@mui/icons-material/Code';
import IosShareIcon from '@mui/icons-material/IosShare';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { Button, TextField, Typography } from '@mui/material';
import { hapticTap } from '../lib/haptics';
import DeathCertificate from '../components/DeathCertificate';

// X/Twitter icon as inline SVG (not in older MUI icon sets)
const XIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.743l7.73-8.835L1.254 2.25H8.08l4.259 5.632zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// LocalStorage bookmarks — private, ephemeral by nature (cleared with cache)
function getBookmarks(): Set<string> {
  try {
    const raw = localStorage.getItem('wipe_bookmarks');
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}
function toggleBookmark(id: string): boolean {
  const bm = getBookmarks();
  if (bm.has(id)) { bm.delete(id); } else { bm.add(id); }
  localStorage.setItem('wipe_bookmarks', JSON.stringify([...bm]));
  return bm.has(id);
}

function PostActions({
  post,
  expandToReply,
  objectType = 'posts',
  parentType = 'posts',
  depth = 0,
}: {
  post: PostData;
  expandToReply?: string;
  objectType?: string;
  parentType?: string;
  /** Current nesting depth (forwarded to <Replies> so it can cap recursion). */
  depth?: number;
}) {
  const userRecord = React.useContext<MyContext>(AuthContext).userRecord;
  const getUserRecord = React.useContext<CacheContextType>(CacheContext).getUserRecord;
  const postAuthorUsername = getUserRecord(post.user_record_id)?.username;
  const [showReplies, setShowReplies] = useState(false);
  const [showVotes, setShowVotes] = useState(false);
  const [copied, setCopied] = useState(false);
  const [bookmarked, setBookmarked] = useState(() => getBookmarks().has(post.id));
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [showEmbedDialog, setShowEmbedDialog] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [showEpitaph, setShowEpitaph] = useState(false);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);

  const handleBookmark = useCallback(() => {
    setBookmarked(toggleBookmark(post.id));
    setMoreMenuAnchor(null);
  }, [post.id]);

  const postUrl = window.location.origin + '/' + objectType + '/' + post.id;

  const toggleReplies = () => {
    setShowReplies((prev) => !prev);
    setShowVotes(false);
  };

  const toggleVotes = () => {
    setShowVotes((prev) => !prev);
    setShowReplies(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(postUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
    setMoreMenuAnchor(null);
  };

  const handleShareX = () => {
    const tweetText = `${(post.value ?? '').slice(0, 120)}${(post.value ?? '').length > 120 ? '…' : ''} ${postUrl}`;
    window.open(
      `https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}`,
      '_blank',
      'noopener,noreferrer',
    );
    setMoreMenuAnchor(null);
  };

  const handleNativeShare = async () => {
    setMoreMenuAnchor(null);
    try {
      await navigator.share({
        text: (post.value ?? '').slice(0, 200),
        url: postUrl,
      });
    } catch { /* cancelled or unsupported */ }
  };

  const handleCopyText = () => {
    const rawText = (post.value ?? '')
      .replace(/@\[([^\]]+)\]\([^)]+\)/g, '@$1')
      .replace(/#\[([^\]]+)\]\([^)]+\)/g, '#$1');
    navigator.clipboard.writeText(rawText);
    setMoreMenuAnchor(null);
  };

  const handleQuote = () => {
    setShowQuoteDialog(true);
    setMoreMenuAnchor(null);
  };

  const embedCode = `<iframe src="${window.location.origin}/embed/posts/${post.id}" width="500" height="260" frameborder="0" style="border:1px solid #e5e5e5;border-radius:8px;" title="wipe.haus post"></iframe>`;
  const handleEmbed = () => {
    setShowEmbedDialog(true);
    setMoreMenuAnchor(null);
  };
  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode).then(() => {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
    });
  };

  // Quick share — on mobile opens native share sheet directly; on
  // desktop copies link to clipboard with toast. One tap, no menu.
  const handleQuickShare = async () => {
    hapticTap();
    const shareText = `${(post.value ?? '').slice(0, 140)}${(post.value ?? '').length > 140 ? '…' : ''}`;
    if ('share' in navigator) {
      try {
        await navigator.share({
          title: 'wipe.haus — a post that won\'t last forever',
          text: shareText,
          url: postUrl,
        });
        return;
      } catch { /* user cancelled; fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(postUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (expandToReply !== undefined) setShowReplies(true);
  }, [expandToReply]);

  return (
    <Box sx={{ color: 'text.primary' }}>
      <Stack direction="row" spacing={0} alignItems="center">
        {/* Replies */}
        <Tooltip title="Replies">
          <IconButton onClick={toggleReplies} size="small" sx={{ gap: 0.5, px: 1 }}>
            <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1 }}>
              {post.reply_count ?? 0}
            </Box>
            <RepliesIcon style={{ width: '18px', height: '18px' }} />
          </IconButton>
        </Tooltip>

        {/* Up vote */}
        {userRecord && (
          <VoteButton parentID={post.id} objectType={objectType} giveTime={true} />
        )}

        {/* Down vote */}
        {userRecord && (
          <VoteButton parentID={post.id} objectType={objectType} giveTime={false} />
        )}

        {/* Vote count */}
        <Tooltip title="Votes">
          <IconButton onClick={toggleVotes} size="small" sx={{ gap: 0.5, px: 1 }}>
            <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1 }}>
              {post.vote_count ?? 0}
            </Box>
            <HourglassFullIcon style={{ width: '18px', height: '18px' }} />
          </IconButton>
        </Tooltip>

        {/* Permanent vote — keep visible since it's a core Wipe mechanic */}
        {userRecord && (
          <VoteButton parentID={post.id} objectType={objectType} makePermanent={true} />
        )}

        {/* Quick share — native share sheet on mobile, copy link on desktop */}
        <Tooltip title="Share">
          <IconButton size="small" sx={{ px: 0.75 }} onClick={handleQuickShare}>
            <IosShareIcon sx={{ width: 17, height: 17 }} />
          </IconButton>
        </Tooltip>

        {/* More actions menu */}
        <Tooltip title="More">
          <IconButton size="small" sx={{ px: 0.75 }} onClick={(e) => setMoreMenuAnchor(e.currentTarget)}>
            <MoreHorizIcon sx={{ width: 18, height: 18 }} />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={moreMenuAnchor}
          open={Boolean(moreMenuAnchor)}
          onClose={() => setMoreMenuAnchor(null)}
          PaperProps={{ elevation: 3, sx: { borderRadius: 2, minWidth: 160 } }}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {userRecord && (
            <MenuItem onClick={handleQuote} dense>
              <ListItemIcon><FormatQuoteIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Quote post</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleBookmark} dense>
            <ListItemIcon>
              {bookmarked ? <BookmarkIcon fontSize="small" color="primary" /> : <BookmarkBorderIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{bookmarked ? 'Remove bookmark' : 'Bookmark'}</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCopyLink} dense>
            <ListItemIcon><LinkIcon style={{ width: 16, height: 16 }} /></ListItemIcon>
            <ListItemText>{copied ? 'Copied!' : 'Copy link'}</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleCopyText} dense>
            <ListItemIcon>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
              </svg>
            </ListItemIcon>
            <ListItemText>Copy text</ListItemText>
          </MenuItem>
          {'share' in navigator && (
            <MenuItem onClick={handleNativeShare} dense>
              <ListItemIcon>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"/>
                </svg>
              </ListItemIcon>
              <ListItemText>Share</ListItemText>
            </MenuItem>
          )}
          <MenuItem onClick={handleShareX} dense>
            <ListItemIcon><XIcon /></ListItemIcon>
            <ListItemText>Share on X</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleEmbed} dense>
            <ListItemIcon><CodeIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Embed</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { setShowEpitaph(true); setMoreMenuAnchor(null); }} dense>
            <ListItemIcon><NoteAddOutlinedIcon fontSize="small" /></ListItemIcon>
            <ListItemText>Epitaph card</ListItemText>
          </MenuItem>
        </Menu>

        <DeathCertificate
          post={post}
          username={postAuthorUsername ?? '?'}
          open={showEpitaph}
          onClose={() => setShowEpitaph(false)}
        />
      </Stack>

      <Snackbar
        open={copied}
        autoHideDuration={2000}
        onClose={() => setCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setCopied(false)} sx={{ width: '100%' }}>
          Link copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Embed Dialog */}
      <Dialog
        open={showEmbedDialog}
        onClose={() => setShowEmbedDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1, fontSize: '1.2rem' }}>
            EMBED POST
          </Box>
          <MuiIconButton size="small" onClick={() => setShowEmbedDialog(false)}>
            <CloseIcon fontSize="small" />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Paste this HTML into any site or blog to embed a live, self-updating
            view of this post. When the post expires, the embed will show it as
            expired — a permanent memento of something fleeting.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={embedCode}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
            onFocus={(e) => (e.target as HTMLTextAreaElement).select()}
            sx={{ mb: 1.5 }}
          />
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleCopyEmbed}
              sx={{ textTransform: 'none' }}
            >
              {embedCopied ? 'Copied!' : 'Copy HTML'}
            </Button>
            <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
              Recommended: 500×260
            </Typography>
          </Box>
          {/* Live preview */}
          <Box sx={{ mt: 2, p: 1, borderRadius: 1, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontFamily: '"VT323", monospace', letterSpacing: 1 }}>
              PREVIEW
            </Typography>
            <Box
              component="iframe"
              src={`${window.location.origin}/embed/posts/${post.id}`}
              sx={{
                width: '100%',
                height: 260,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'background.paper',
              }}
              title="Embed preview"
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Quote Post Dialog */}
      <Dialog
        open={showQuoteDialog}
        onClose={() => setShowQuoteDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1, fontSize: '1.2rem' }}>
            QUOTE POST
          </Box>
          <MuiIconButton size="small" onClick={() => setShowQuoteDialog(false)}>
            <CloseIcon fontSize="small" />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <SubmitPostComponent
            objectType="posts"
            defaultComposition={postUrl}
            onSubmit={() => setShowQuoteDialog(false)}
          />
        </DialogContent>
      </Dialog>

      {showReplies && (
        <Stack direction="column">
          {userRecord && (
            <>
              <Box sx={{ px: 1, pt: 1, pb: 0.25, fontSize: '0.78rem', color: 'text.disabled' }}>
                Replying to{' '}
                <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
                  @{postAuthorUsername ?? '…'}
                </Box>
              </Box>
              <SubmitPostComponent
                parentID={post.id}
                objectType={'posts'}
                parentType={parentType}
              />
            </>
          )}
          <Replies
            parentObject={post}
            expandToReply={expandToReply}
            setShowReplies={setShowReplies}
            depth={depth}
          />
        </Stack>
      )}
      {showVotes && (
        <VotesList recordID={post.id} isPost={true} showMenu={false} />
      )}
    </Box>
  );
}

export default PostActions;
